/**
 * @module web/userManager
 * @private
 */

import { cache } from "../../persistence/cache";
import { memoryCache } from "../../persistence/memoryCache";
import { layoutTokenStore } from "../../store";

/**
 * Extends userManager.getDevices method to check if the requested devices is already stored in the cache, and will use
 * the local version instead of making the call to the backend.
 * @param {function} originalFunction userManager.getDevices method that will be wrapped with the previous cache check
 * @returns {function} A new getDevices function that will check the local cache before making a network request without
 * changing the getDevices method itself.
 */
export function fromDevicesCache(originalFunction) {
  return async function(config = {}) {
    if (!config.avoidCache && this.localCache && this.localCache.devices) {
      let devices = (await cache.get("devices", { database: "devices" })) || {};
      if (Object.keys(devices).length === 0) {
        devices.userDevices = await originalFunction.call(this);
      }
      return devices.userDevices;
    } else {
      return await originalFunction.call(this);
    }
  };
}

/**
 * Extends userManager.getDevices method to store the received data in the local cache after the network request.
 * @param {function} originalFunction userManager.getDevices method that will be wrapped with the caching logic
 * @returns {function} A new getDevices function that will store the received data in the cache without modifying
 * the getDevices method itself.
 */
export function toDevicesCache(originalFunction) {
  return async function(...args) {
    const response = await originalFunction.call(this, ...args);
    if (this.localCache && this.localCache.devices) {
      cache.set(
        "devices",
        { userDevices: response },
        {
          database: "devices",
          expiresIn: this.devicesCacheTime
        }
      );
    }
    return response;
  };
}

/**
 * Extends userManager.addDevice method to store the newly added device in cache after the network request. This
 * function updates the devices cache without making a devices get request, it just adds the newly added device to the
 * list of cached devices locally. In case the local cache had already the maximum amount of devices, the cache will be
 * cleared and only the newly added device will be stored in cache. This is done because at least one of the previously
 * cached devices is not registered in the user's account anymore, but it's impossible to know which one without making
 * an extra devices call.
 * @param {function} originalFunction userManager.addDevice method that will be wrapped with the caching logic
 * @return {function} A new addDevice function that will store new device data in cache without modifying the addDevice
 * method itself.
 */
export function addDeviceToCache(originalFunction) {
  return async function(...args) {
    const response = await originalFunction.call(this, ...args);
    if (this.localCache && this.localCache.devices) {
      let { userDevices } = await cache.get("devices", { database: "devices" });
      if (!userDevices || userDevices.length === this.maxUserDevices) {
        // Init the array if there is no previously cached list of devices OR
        // Clear the cache because there were already max amount of devices cached but the list is not valid anymore
        userDevices = [];
      }
      userDevices.push(args[0]);
      cache.set(
        "devices",
        { userDevices },
        {
          database: "devices",
          expiresIn: this.devicesCacheTime
        }
      );
    }
    return response;
  };
}

/**
 * Extends userManager.getDevices method to check if the requested devices is already stored in memory, and will use
 * the local version instead of making the call to the backend.
 * @param {function} originalFunction userManager.getDevices method that will be wrapped with the previous memory check
 * @returns {function} A new getDevices function that will check the memory before making a network request without
 * changing the getDevices method itself.
 */
export function fromDevicesMemoryCache(originalFunction) {
  return async function(config = {}) {
    if (!config.avoidCache && this.localCache && this.localCache.memoryCache) {
      return memoryCache.get("devices") || (await originalFunction.call(this, config));
    } else {
      return await originalFunction.call(this, config);
    }
  };
}

/**
 * Extends userManager.getDevices method to store the received data in memory after the network request.
 * @param {function} originalFunction userManager.getDevices method that will be wrapped with the memoizing logic
 * @returns {function} A new getDevices function that will store the received data in memory without modifying
 * the getDevices method itself.
 */
export function toDevicesMemoryCache(originalFunction) {
  return async function(...args) {
    const response = await originalFunction.call(this, ...args);
    if (this.localCache && this.localCache.memoryCache) {
      memoryCache.set("devices", response);
    }
    return response;
  };
}

/**
 * Extends userManager.addDevice method to store the newly added device in memory after the network request. This
 * function updates the devices memory without making a devices get request, it just adds the newly added device to the
 * list of cached devices locally. In case the local memory had already the maximum amount of devices, the memory will
 * be cleared and only the newly added device will be stored in memory. This is done because at least one of the
 * previously cached devices is not registered in the user's account anymore, but it's impossible to know which one
 * without making an extra devices call.
 * @param {function} originalFunction userManager.addDevice method that will be wrapped with the memoizing logic
 * @return {function} A new addDevice function that will store new device data in memory without modifying the addDevice
 * method itself.
 */
export function addDeviceToMemoryCache(originalFunction) {
  return async function(...args) {
    const response = await originalFunction.call(this, ...args);
    if (this.localCache && this.localCache.memoryCache) {
      let devicesInMemory = memoryCache.get("devices");
      if (!devicesInMemory || devicesInMemory.length === this.maxUserDevices) {
        // Init the array if there is no previously cached list of devices OR
        // Clear the cache because there were already max amount of devices cached but the list is not valid anymore
        devicesInMemory = [];
      }
      devicesInMemory.push(args[0]);
      memoryCache.set("devices", devicesInMemory);
    }
    return response;
  };
}

/**
 * Extends userManager.get method to check if the requested user is already stored in the cache, and will use
 * the local version instead of making the call to the backend.
 * @param {function} originalFunction userManager.get method that will be wrapped with the previous cache check
 * @returns {function} A new get function that will check the local cache before making a network request without
 * changing the userManager.get method itself.
 */
export function fromUserCache(originalFunction) {
  return async function(config = {}) {
    if (!config.avoidCache && this.localCache && this.localCache.auth) {
      let user = await cache.get("user", { database: "auth" });
      if (!user) {
        user = await originalFunction.call(this, config);
      }
      return user;
    } else {
      return await originalFunction.call(this, config);
    }
  };
}

/**
 * Extends authenticationManager.login or authenticationManager.signup method to store the received data in the local cache after the network request.
 * @param {Function} originalFunction - Login or signup method that will be wrapped with the caching logic.
 * @returns {Function} A new login function that will store the received data in the cache without modifying
 * the login/signup method itself.
 */
export function toUserCache(originalFunction) {
  return async function(userInfo, extraParams) {
    const response = await originalFunction.call(this, userInfo, extraParams);
    if (this.localCache && this.localCache.auth && response) {
      cache.set("user", response, {
        database: "auth",
        expiresIn: this.userCacheTime
      });
    }
    return response;
  };
}

/**
 * Extends authenticationManager.logout method to clear the locally cached user information after the logout happens.
 * @param {function} originalFunction logout method that will be wrapped with the cache clearing logic.
 * @returns {function} A new logout function that will remove the user data from the cache after logout happens.
 */
export function clearUserCache(originalFunction) {
  return async function(isResetXToken = false) {
    if (this.localCache && this.localCache.auth) {
      cache.remove("user", {
        database: "auth"
      });
    }
    await originalFunction.call(this, isResetXToken);
  };
}

/**
 * Check if the user playback preferences is cached locally to returned the cached data, and if not to perform a GET
 * request to get the user preferences from the web service
 * @param {function} originalFunction userManager.getPreferences method that will be wrapped with the previous cache check
 * @returns {function} A new getPreferences function that will check the local cache before making a network request without
 * changing the getPreferences method itself.
 */
export function fromPreferencesCache(originalFunction) {
  return async function() {
    if (this.localCache && this.localCache.preferences) {
      let preferences = await cache.get("preferences", {
        database: "preferences"
      });
      if (!preferences) {
        return await originalFunction.call(this);
      }
      return preferences;
    } else {
      let preferences = await originalFunction.call(this);
      return preferences;
    }
  };
}

/**
 * Save the user playback preferences object into the cache along with GET requests to the preferences Web service
 *
 * @param {function} originalFunction userManager.getPreferences method that will be wrapped with the caching logic
 * for user playback preferences
 * @returns {function} A new getPreferences function that will store the received playback
 * preferences data in the cache
 */
export function toPreferencesCache(originalFunction) {
  return async function(params = {}) {
    const response = await originalFunction.call(this, params);
    if (this.localCache && this.localCache.preferences) {
      cache.set("preferences", response, {
        database: "preferences",
        expiresIn: this.userPreferencesCacheTime
      });
    }
    return response;
  };
}

/**
 * Update the user playback preferences object into the cache along with POST requests to the preferences Web service
 *
 * @param {function} originalFunction userManager.updatePreferences method that will be wrapped with the caching
 * logic for user playback preferences
 * @returns {function} A new updatePreferences function that will store the received playback preferences data in the cache
 */
// FIXME this function is an 99% copy of the previous one, toPreferencescache. It should be removed.
export function toUpdatePreferencesCache(originalFunction) {
  return async function(params = {}) {
    const response = await originalFunction.call(this, params);
    if (this.localCache && this.localCache.preferences) {
      cache.set("preferences", response, {
        database: "preferences",
        expiresIn: this.userPreferencesCacheTime
      });
    }
    return response.data;
  };
}

/**
 * Wraps the getPreferences function and adds a check to find if the data is already cached in memory. If it is, there
 * won't be a call to backend and the cache value will be returned directly.
 * @param {function} originalFunction userManager.getPreferences method that will be wrapped with the caching logic
 * for user playback preferences
 * @returns {function} A new getPreferences function that will have a previous check to see if the requested value is
 * already present in memory.
 */
export function fromPreferencesMemoryCache(originalFunction) {
  return async function() {
    if (this.localCache && this.localCache.memoryCache) {
      let preferences = memoryCache.get("preferences");
      if (!preferences) {
        return await originalFunction.call(this);
      }
      return preferences;
    } else {
      return await originalFunction.call(this);
    }
  };
}

/**
 * Wraps the getPreferences function and adds the logic to store the response from the backend in memory.
 * @param {function} originalFunction userManager.getPreferences method that will be wrapped with the caching logic
 * for user playback preferences
 * @returns {function} A new getPreferences function that will have an additional step to save the received data into
 * memory.
 */
export function toPreferencesMemoryCache(originalFunction) {
  return async function(params = {}) {
    const response = await originalFunction.call(this, params);
    if (this.localCache && this.localCache.memoryCache) {
      memoryCache.set("preferences", response);
    }
    return response;
  };
}

/**
 * Extends userManager.getBilling method to store the received data in the local cache after the network request.
 * @param {function} originalFunction getBilling method that will be wrapped with the caching logic
 * @returns {function} A new getBilling function that will store the received data in the cache without modifying
 * the getBilling method itself.
 */
export function toBillingCache(originalFunction) {
  return async function() {
    const response = await originalFunction.call(this);
    if (this.localCache && this.localCache.auth) {
      let cachedUser = await cache.get("user", { database: "auth" });
      cachedUser = { ...cachedUser, billing: response };
      cache.set("user", cachedUser, {
        database: "auth",
        expiresIn: this.userCacheTime
      });
    }
    return response;
  };
}

/**
 * Extends userManager.getBilling method to check if the requested billing information is already stored in the cache,
 * and will use the local version instead of making the call to the backend.
 * @param {function} originalFunction userManager.getBilling method that will be wrapped with the previous cache check
 * @returns {function} A new getBilling function that will check the local cache before making a network request without
 * changing the userManager.getBilling method itself.
 */
export function fromBillingCache(originalFunction) {
  return async function(config = {}) {
    if (!config.avoidCache && this.localCache && this.localCache.auth) {
      let user = await cache.get("user", { database: "auth" });
      if (!user || !user.billing) {
        const billingInformation = await originalFunction.call(this);
        user = { ...user, billing: billingInformation };
      }
      return user.billing;
    } else {
      return await originalFunction.call(this);
    }
  };
}

/**
 * Extends userManager.setLanguage and userManager.changeParentalLevel methods to reset layout tokens to be able to request new layout data for new languages or new parental level.
 * @param {Function} originalFunction - Currently, userManager.setLanguage and userManager.changeParentalLevel methods.
 * @public
 */
export function clearLayoutHeaders(originalFunction) {
  return async function(...params) {
    layoutTokenStore.clear();
    return originalFunction.call(this, ...params);
  };
}

export function toSubscriptionsStatusMemoryCache(originalFunction) {
  return async function(extraParams) {
    const response = await originalFunction.call(this, extraParams);
    if (response) {
      cache.set("deepAddOns", response, {
        database: "deepAddOns",
        expiresIn: this.deepAddOnsCacheTime
      });
    }
    return response;
  };
}

export function fromSubscriptionsStatusMemoryCache(originalFunction) {
  return async function(config = {}) {
    if (!config.avoidCache) {
      let deepAddOns = await cache.get("deepAddOns", { database: "deepAddOns" });
      if (!deepAddOns) {
        deepAddOns = await originalFunction.call(this, config);
      }
      return deepAddOns;
    } else {
      return await originalFunction.call(this, config);
    }
  };
}
